import React from 'react';
import { Drawer, Tag, Collapse, Table, Descriptions, Space, Typography, Badge, Empty, Spin } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { ChainDetail, ChainNode, ChainNodeDep } from '../../../services/devApi';

const { Text } = Typography;

const statusColorMap: Record<string, string> = {
  LIVE_VERIFIED: 'green',
  INTEGRATION_VERIFIED: 'blue',
  PARTIALLY_CONNECTED: 'orange',
  MODULES_EXIST_BUT_CHAIN_BROKEN: 'red',
  NOT_STARTED: 'default',
  ready: 'green',
  blocked: 'red',
  pending: 'default',
  wip: 'processing',
  done: 'success',
};

const evidenceColorMap: Record<string, string> = {
  LIVE_VERIFIED: 'green',
  INTEGRATION_VERIFIED: 'cyan',
  E2E_VERIFIED: 'blue',
  UNIT_VERIFIED: 'geekblue',
  MODULE_EXISTS: 'orange',
  ROUTE_REGISTERED: 'gold',
  UNKNOWN: 'default',
};

const layerLabels: Record<string, string> = {
  dependency: '依賴主檔層',
  process: '流程層',
  side_effect: '副作用層',
};

interface Props {
  visible: boolean;
  chain: ChainDetail | null;
  loading: boolean;
  onClose: () => void;
}

function formatDate(val: string | null | undefined): string {
  if (!val) return '--';
  const d = new Date(val);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${mm}-${dd} ${hh}:${min}`;
}

const ChainDetailDrawer: React.FC<Props> = ({ visible, chain, loading, onClose }) => {
  const drawerWidth = typeof window !== 'undefined' && window.innerWidth < 768 ? '100%' : 720;

  if (!visible) return null;

  const nodeColumns: ColumnsType<ChainNode> = [
    {
      title: 'Node ID',
      dataIndex: 'nodeId',
      key: 'nodeId',
      width: 120,
      render: (id: string) => <span className="font-mono text-xs">{id}</span>,
    },
    {
      title: '標籤',
      dataIndex: 'label',
      key: 'label',
      width: 140,
      ellipsis: true,
    },
    {
      title: '模組',
      dataIndex: 'module',
      key: 'module',
      width: 80,
      render: (mod: string) => mod ? <span className="font-mono">{mod}</span> : <span className="text-gray-400">--</span>,
    },
    {
      title: 'FormId',
      dataIndex: 'formId',
      key: 'formId',
      width: 80,
      render: (fid: string) => fid ? <span className="font-mono">{fid}</span> : <span className="text-gray-400">--</span>,
    },
    {
      title: '核准',
      dataIndex: 'requiresApproval',
      key: 'requiresApproval',
      width: 60,
      align: 'center',
      render: (val: boolean) => val ? <Tag color="volcano">需核准</Tag> : null,
    },
    {
      title: '狀態',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => (
        <Tag color={statusColorMap[status] || 'default'}>
          {status.replace(/_/g, ' ')}
        </Tag>
      ),
    },
    {
      title: '證據',
      dataIndex: 'evidenceLevel',
      key: 'evidenceLevel',
      width: 120,
      render: (level: string) => (
        <Tag color={evidenceColorMap[level] || 'default'}>
          {level.replace(/_/g, ' ')}
        </Tag>
      ),
    },
    {
      title: '依賴層級',
      dataIndex: 'dependencyLevel',
      key: 'dependencyLevel',
      width: 80,
      align: 'center',
    },
    {
      title: '依賴節點',
      dataIndex: 'dependsOn',
      key: 'dependsOn',
      width: 160,
      render: (deps: ChainNodeDep[]) => {
        if (!deps || deps.length === 0) return <span className="text-gray-400">--</span>;
        return (
          <Space size={2} wrap>
            {deps.map((dep) => (
              <Tag key={dep.dependsOnNode} style={{ fontSize: 11 }}>
                {dep.dependsOnNode}
              </Tag>
            ))}
          </Space>
        );
      },
    },
  ];

  const layers = ['dependency', 'process', 'side_effect'] as const;

  const collapseItems = chain
    ? layers.map((layer) => {
        const nodes = (chain.nodes || []).filter((n) => n.layer === layer);
        return {
          key: layer,
          label: (
            <Space>
              <Text strong>{layerLabels[layer] || layer}</Text>
              <Badge count={nodes.length} style={{ backgroundColor: nodes.length > 0 ? '#1677ff' : '#8c8c8c' }} />
            </Space>
          ),
          children:
            nodes.length === 0 ? (
              <Empty description="此層無節點" />
            ) : (
              <Table<ChainNode>
                columns={nodeColumns}
                dataSource={nodes}
                rowKey="nodeId"
                size="small"
                pagination={false}
                scroll={{ x: 800 }}
              />
            ),
        };
      })
    : [];

  return (
    <Drawer
      title={chain ? `${chain.chainName}` : '鏈條詳情'}
      placement="right"
      width={drawerWidth}
      open={visible}
      onClose={onClose}
      destroyOnClose
    >
      {loading || !chain ? (
        <div className="flex justify-center items-center h-64">
          <Spin size="large" />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Header info */}
          <Descriptions column={2} bordered size="small">
            <Descriptions.Item label="Chain ID">
              <span className="font-mono">{chain.chainId}</span>
            </Descriptions.Item>
            <Descriptions.Item label="領域">
              <Tag>{chain.businessDomain}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="狀態">
              <Tag color={statusColorMap[chain.currentStatus] || 'default'}>
                {chain.currentStatus.replace(/_/g, ' ')}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="證據等級">
              <Tag color={evidenceColorMap[chain.evidenceLevel] || 'default'}>
                {chain.evidenceLevel.replace(/_/g, ' ')}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="起始表單">
              <span className="font-mono">{chain.startFormId}</span>
            </Descriptions.Item>
            <Descriptions.Item label="更新時間">
              {formatDate(chain.updatedAt)}
            </Descriptions.Item>
            <Descriptions.Item label="業務目標" span={2}>
              {chain.businessGoal || '--'}
            </Descriptions.Item>
            {chain.sharedBase && chain.sharedBase.length > 0 && (
              <Descriptions.Item label="共享基底" span={2}>
                <Space size={4} wrap>
                  {chain.sharedBase.map((sb) => (
                    <Tag key={sb}>{sb}</Tag>
                  ))}
                </Space>
              </Descriptions.Item>
            )}
            {chain.requiresWave0 && (
              <Descriptions.Item label="Wave0" span={2}>
                <Tag color="warning">需要 Wave0</Tag>
              </Descriptions.Item>
            )}
          </Descriptions>

          {/* Node layers */}
          <Collapse defaultActiveKey={['dependency', 'process', 'side_effect']} items={collapseItems} />
        </div>
      )}
    </Drawer>
  );
};

export default ChainDetailDrawer;
